import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {BrandKitSwatchCluster} from '@Components/brand-kit-swatch-cluster/brand-kit-swatch-cluster';
import type {BrandKitDebugDetails, BrandKitDebugFontDetails} from '@Components/ai-chat-window/lib/ai-chat-brand-kit-debug.library';
import {getBrandKitDebugDetails} from '@Components/ai-chat-window/lib/ai-chat-brand-kit-debug.library';
import {Text, TextSize} from '@Components/text';
import {RequestPreferenceCard} from '../request-preference-card/request-preference-card';
import type {BrandKitPreferenceProps} from './brand-kit-preference.types';
import {
  BRAND_KIT_LOGO_THUMB_LABEL,
  BRAND_KIT_PREFERENCE_LABEL,
  BRAND_KIT_PREFERENCE_LOADING_VALUE,
  BRAND_KIT_PREFERENCE_UNRESOLVED_VALUE,
} from './brand-kit-preference.types';
import styles from './brand-kit-preference.module.scss';

export function BrandKitPreference({brandKitId}: BrandKitPreferenceProps): ReactElement {
  const [details, setDetails] = useState<BrandKitDebugDetails | null>(null);
  const [hasFailedToResolve, setHasFailedToResolve] = useState(false);

  useEffect(() => {
    let isForCurrentBrandKit = true;
    setDetails(null);
    setHasFailedToResolve(false);

    void getBrandKitDebugDetails(brandKitId).then(
      (resolvedDetails) => {
        if (isForCurrentBrandKit) {
          setDetails(resolvedDetails);
        }
      },
      (error: unknown) => {
        console.error('Failed to resolve the pinned brand kit for the super user debug line', error);
        if (isForCurrentBrandKit) {
          setHasFailedToResolve(true);
        }
      }
    );

    return (): void => {
      isForCurrentBrandKit = false;
    };
  }, [brandKitId]);

  const getAssetThumb = (label: string, imageURL: string): ReactElement => {
    return (
      <a key={label} className={styles.asset} href={imageURL} target="_blank" rel="noreferrer">
        <span className={styles.thumbFrame}>
          <img className={styles.thumb} src={imageURL} alt="" />
        </span>
        <Text val={label} size={TextSize.XS} className={styles.assetLabel} />
      </a>
    );
  };

  const getSpecimenThumbs = (fonts: BrandKitDebugFontDetails[]): ReactElement[] => {
    return fonts.flatMap((font) => {
      return font.specimenURL === null ? [] : [getAssetThumb(`${font.role} · ${font.name}`, font.specimenURL)];
    });
  };

  const getAssets = (resolvedDetails: BrandKitDebugDetails): ReactElement | null => {
    const thumbs = [
      ...(resolvedDetails.logoURL === null ? [] : [getAssetThumb(BRAND_KIT_LOGO_THUMB_LABEL, resolvedDetails.logoURL)]),
      ...getSpecimenThumbs(resolvedDetails.fonts),
    ];

    if (thumbs.length === 0) {
      return null;
    }

    return <span className={styles.assets}>{thumbs}</span>;
  };

  if (details === null) {
    return (
      <RequestPreferenceCard
        label={BRAND_KIT_PREFERENCE_LABEL}
        value={hasFailedToResolve ? BRAND_KIT_PREFERENCE_UNRESOLVED_VALUE : BRAND_KIT_PREFERENCE_LOADING_VALUE}
        detail={brandKitId}
      />
    );
  }

  return (
    <RequestPreferenceCard
      label={BRAND_KIT_PREFERENCE_LABEL}
      value={details.name}
      detail={brandKitId}
      trailing={<BrandKitSwatchCluster logoURL={details.logoURL ?? undefined} colorHexes={details.colorHexes} />}
    >
      {getAssets(details)}
    </RequestPreferenceCard>
  );
}
